import { Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components'
import { InvalidContainerNumberInfoMessage } from '@planning/pages/ContainerMasterData/components/Organisms/ContainerMasterDataForm'
import { AddUnitInfoFormWrapperStore } from '@planning/pages/Order/components/StepperForm/stores/AddUnitInfoFormWrapperStore'
import { CreateOrderPageViewStore } from '@planning/pages/Order/stores/CreateOrderPageViewStore'
import { validateContainerNumber } from '@planning/rt-stores/helpers'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  notAllowPlusMinusPointSign,
  onlyAllowPositiveInt,
} from 'modules/utils/src/lib/only-allow-int'
import { FC } from 'react'
import { ContainerCreationFormStore } from '../stores/ContainerCreationFormStore'

interface Props {
  formStore: ContainerCreationFormStore
  viewStore: AddUnitInfoFormWrapperStore
  createOrderPageViewStore: CreateOrderPageViewStore
}

const BoxtechAlert: FC<{
  boxtechError: boolean
}> = ({ boxtechError }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const boxtechInfoText = t(
    'weUseBoxtechToFindContainerDetailsForYou',
    'We use Boxtech to find container details for you.',
  )
  const boxtechErrorText = t('noUnitFoundAddThemManually', 'No unit found! Add them manually.')

  return (
    <Box>
      <Stack
        gap={theme.customSpacing.xxs}
        direction={'row'}
        alignItems={'center'}
        sx={{
          backgroundColor: boxtechError
            ? theme.palette.warning.lighter
            : theme.palette['grey'][200],
          color: boxtechError ? theme.palette.warning.dark : theme.palette['grey'][700],
          padding: `${theme.customSpacing.xs}`,
          borderRadius: theme.customRadius.small,
          margin: `${theme.customSpacing.xs} 0`,
        }}
      >
        <Typography variant='caption'>
          {boxtechError ? boxtechErrorText : boxtechInfoText}
        </Typography>
      </Stack>
    </Box>
  )
}

export const ContainerCreationForm = observer((props: Props) => {
  const { formStore, viewStore, createOrderPageViewStore } = props
  const { t } = useTranslate()

  const getContainerAttributes = async () => {
    const containerInput = formStore.containerNumber.value.trim().toUpperCase()
    formStore.containerNumber.setValue(containerInput)

    try {
      viewStore.setBoxtechLoading(true)
      viewStore.reset()
      await viewStore.fetchExistingContainers(containerInput)

      if (validateContainerNumber(containerInput)) {
        await viewStore.fetchContainerAttributes(containerInput)
      }

      if (viewStore.containerAttributes?.isoCode)
        formStore.isoCode.setValue(viewStore.containerAttributes.isoCode)
      if (viewStore.containerAttributes?.tare)
        formStore.tare.setValue(viewStore.containerAttributes.tare.toString())
      if (viewStore.containerAttributes?.maxGross)
        formStore.maxGross.setValue(viewStore.containerAttributes.maxGross.toString())

      viewStore.setHasFetchedContainer(true)
    } catch {
      viewStore.setBoxtechError(true)
    } finally {
      viewStore.setBoxtechLoading(false)
    }
  }

  const getContainerNumberErrorText = (error?: string) => {
    if (!error) return ''

    if (error === 'fieldIsRequired') {
      return t('fieldIsRequired', 'Field is required.')
    }

    if (error === 'invalidContainerNumber') {
      return t(
        'invalidContainerNumber',
        'Invalid container number. Must follow format ABCU1234567.',
      )
    }

    return t(error)
  }

  const containerNumber = computed(() => formStore.containerNumber.value).get()
  const showInvalidContainerNumberInfoMessage = computed(
    () => !validateContainerNumber(containerNumber) && containerNumber.length > 0,
  ).get()

  return (
    <Box p={1}>
      <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
        {t('containerDetails', 'Container details')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            label={t('containerNo.', 'Container no.')}
            required
            variant='outlined'
            fullWidth
            value={formStore.containerNumber.value}
            onChange={e => formStore.containerNumber.setValue(e.target.value.toUpperCase())}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            InputProps={{
              readOnly: false,
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {viewStore.isBoxtechLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Button
                      type='button'
                      color='inherit'
                      variant='contained'
                      sx={{ flex: 'none' }}
                      data-cy='find-details-container-btn'
                      onClick={getContainerAttributes}
                    >
                      {t('findDetails', 'Find details')}
                    </Button>
                  )}
                </Box>
              ),
            }}
            error={!!formStore.containerNumber.error}
            helperText={getContainerNumberErrorText(formStore.containerNumber.error)}
          />

          {viewStore.boxtechError && !showInvalidContainerNumberInfoMessage && (
            <BoxtechAlert boxtechError={viewStore.boxtechError} />
          )}
          {showInvalidContainerNumberInfoMessage && <InvalidContainerNumberInfoMessage />}
        </Grid>

        {viewStore.hasFetchedContainer && (
          <>
            {createOrderPageViewStore.isoCodeMappingFieldRenderFn && (
              <Grid size={12}>
                {createOrderPageViewStore.isoCodeMappingFieldRenderFn({
                  field: formStore.isoCode,
                  label: t('isoCode', 'ISO code'),
                  fullWidth: true,
                  required: true,
                  disabled: false,
                })}
              </Grid>
            )}

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 5,
              }}
            >
              <TextField
                label='Tare weight'
                fullWidth
                type='number'
                value={formStore.tare.value}
                onKeyDown={notAllowPlusMinusPointSign}
                onChange={e => {
                  onlyAllowPositiveInt(e)
                  formStore.tare.setValue(e.target.value)
                }}
                InputProps={{
                  endAdornment: (
                    <CustomInputAdornment position='end' color='secondary'>
                      kg
                    </CustomInputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 5,
              }}
            >
              <TextField
                label='Max gross weight'
                fullWidth
                type='number'
                value={formStore.maxGross.value}
                onKeyDown={notAllowPlusMinusPointSign}
                onChange={e => {
                  onlyAllowPositiveInt(e)
                  formStore.maxGross.setValue(e.target.value)
                }}
                InputProps={{
                  endAdornment: (
                    <CustomInputAdornment position='end' color='secondary'>
                      kg
                    </CustomInputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={12}>
              <CompanyAutocomplete
                required={!createOrderPageViewStore.isFromRestowPage}
                label={t('operator', 'Operator')}
                disabled={!!createOrderPageViewStore.inheritedOperatorShortName}
                selectedCompanyShortName={formStore.operator.value}
                handleOnChange={operator => formStore.operator.setValue(operator?.shortName ?? '')}
                filterByTypes={[CompanyType.ShippingLine]}
                dataCy='operator-autocomplete'
                error={!!formStore.operator.error}
                helperText={
                  formStore.operator.error ? t('fieldIsRequired', 'Field is required.') : ''
                }
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
})
